import { useMemo, useState } from "react";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useQueryGetModelPropertiesList } from "src/queries/administrator/modelProperty.queries";
import type { ModelPropertyRecord } from "src/services/administrator/modelPropertyService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import AddModelPropertiesFormModal from "./AddModelPropertiesFormModal";
import AddModelPropertiesTable from "./AddModelPropertiesTable";
import "../../Customers/Customers.css";

const DEFAULT_PAGINATION = { page: 0, size: 10 };

interface ModelPropertySorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["active", "true", "1"].includes(value.toLowerCase());
  return false;
};

const getName = (value: unknown) => {
  if (!value || typeof value !== "object") return "";
  const item = value as {
    name?: string;
    code?: string;
    propertyNameComm?: string;
    propertyNameInternal?: string;
  };
  return item.propertyNameComm ?? item.name ?? item.propertyNameInternal ?? item.code ?? "";
};

const getUnitBrand = (record: ModelPropertyRecord) => record.make ?? record.model?.make;

const AddModelProperties = () => {
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<ModelPropertySorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<ModelPropertyRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const canEdit = hasAdministrationPermission("edit");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sortDir ?? "DESC",
      sortBy: sorting.sortBy ?? "ID",
    }),
    [pagination.page, pagination.size, sorting.sortBy, sorting.sortDir],
  );

  const { data, isLoading } = useQueryGetModelPropertiesList(queryParams, { enabled: true });
  const response = data?.data?.response;
  const records = useMemo(
    () =>
      response?.modelProperties ??
      response?.data ??
      response?.items ??
      response?.content ??
      [],
    [response],
  );

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return records;

    return records.filter((record) =>
      [
        getName(getUnitBrand(record)),
        getName(record.model),
        getName(record.property),
        isActive(record.active) ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : (response?.totalItems ?? response?.totalElements ?? response?.total ?? records.length);

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="add_model_properties"
        buttonKey="create"
        onClick={() => {
          setFormRecord(null);
          setIsFormOpen(true);
        }}
        showBtn={canEdit}
        buttonProps={{ className: "!text-gray2 !w-[94px] !h-[40px]" }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <AddModelPropertiesTable
          data={filteredRecords}
          isLoading={isLoading}
          pagination={{ ...pagination, total: totalCount }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          onEdit={(record) => {
            setFormRecord(record);
            setIsFormOpen(true);
          }}
        />
      </div>

      <AddModelPropertiesFormModal
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />
    </section>
  );
};

export default AddModelProperties;
